import {runInAction, makeAutoObservable, toJS} from "mobx";
import { CognitoIdToken, CognitoAccessToken, CognitoUserSession, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import AWSCognitoProvider from "../lib/AWSCognitoProvider";
import {
  fetchPasswordValidation,
  registerPasswordChange
} from "../queries/authentication";
import { getConfig, DEV } from "../lib/configMgr";
import { retrieveAndClearVerifier } from '../lib/pkce';
import {RequestBuilder, toJSON} from "../queries/request";
import {isTruthy, wait} from "../lib/utils";
import * as Sentry from "@sentry/react";
import { NotAuthorized } from "../queries/errors";
import {toast} from "react-toastify";
import Analytics from "../lib/Analytics";


const { ExternalRESTAPI, AWSCognito: { region: COGNITO_REGION, userPoolID: COGNITO_POOL_ID, clientID: COGNITO_CLIENT_ID } } = getConfig();
const API_KEY = 'custom:api-key'

// A cached Cognito session object existing doesn't mean its JWT is still valid — check
// the token's own exp claim rather than trusting "we have a session" as a proxy for it.
const isSessionExpired = (session) => {
  const exp = session?.idToken?.payload?.exp;
  return typeof exp === 'number' && exp * 1000 <= Date.now();
};

class UserStore {

  _authProvider = null;
  _cognitoUser = null;
  _cognitoUserSession = null;
  _tokenExpired = true;
  _apiKey = null;
  _restoring = false;
  _refreshPromise = null;
  _ready = true;
  _appStatusStore = null;
  _checkingAuthorization = false;
  _isAuthorized = null;
  _showAdmin = false;
  _loginMethod = null; // 'sso' or 'password'

  constructor(appStatusStore) {
    this._appStatusStore = appStatusStore;
    this._showAdmin = isTruthy(localStorage['plex-show-admin']);
    makeAutoObservable(this);
    this.restoreSession()
      .then((session)=>session);
  }

  _initProvider = () => {
    if (!this.authProvider) {
      this.authProvider = new AWSCognitoProvider(COGNITO_REGION, COGNITO_POOL_ID, COGNITO_CLIENT_ID);
    }
    return Promise.resolve(this.authProvider);
  };

  get authData() {
    // Interface provides .headers, .refresh() and .jwtToken
    return this;
  }

  get headers() {
    return {"Authorization": `Bearer ${this.jwtToken}`};
  }

  logout = () => {
    if (this.userName) {
      if (this.authProvider) {
        this.authProvider.signOut(this.userName);
      }
      Analytics.event(Analytics.EVENTS.SIGNOUT, { user: this.userName });
    }
    Sentry.getCurrentScope().setUser(null);
    // Preserve tutorial state across logout/login cycles
    const tutorialState = localStorage.getItem('plex-tutorial-state');
    // remove any persistent session
    localStorage.clear();
    sessionStorage.clear();
    // Restore tutorial state
    if (tutorialState) {
      localStorage.setItem('plex-tutorial-state', tutorialState);
    }
    this.authProvider = null;
    this.session = null;
    this.user = null;
    this.tokenExpired = true;
    this.apiKey = null;
    this.isAuthorized = null;
  };

  login = async (username, password, navigate, confirmationCode) => {
    this.ready = false;
    this.session = null;
    this.isAuthorized = null;
    this._loginMethod = 'password';
    const login_func = provider => provider.signIn(username, password, navigate, confirmationCode)
      .then(this.checkAuthorization)
      .then(session => {
        Analytics.event(Analytics.EVENTS.SIGNIN, { user: this.userName, tenant: this.tenantID });
        Analytics.identify(this.userName, this.tenantID);
        return session;
      })
      .catch(error => {
        // For login failures, we want to propagate the error to the UI
        // Don't use onSessionError here since it suppresses NotAuthorizedException
        if (error?.name === "TooManyRequestsException") {
          return wait(1000).then(() => this.login(username, password, navigate, confirmationCode));
        }
        // Clear any partial Cognito session data
        if (this.authProvider) {
          this.authProvider.signOut(null);
        }
        this.session = null;
        this.user = null;
        this.isAuthorized = false;
        this.ready = true;
        Analytics.event(Analytics.EVENTS.SIGNIN_FAILURE, { user: username, error_name: error?.name });
        // Re-throw the error so it reaches the UI catch handler
        throw error;
      });
    if (this.authProvider) {
      return login_func(this.authProvider);
    }
    return this._initProvider().then(login_func);
  };

  loginWithTokens = async (idTokenJwt, accessTokenJwt, refreshTokenJwt = null) => {
    this._loginMethod = 'sso';
    const { AWSCognito: { userPoolID, clientID } } = getConfig();

    const idToken = new CognitoIdToken({ IdToken: idTokenJwt });
    const accessToken = new CognitoAccessToken({ AccessToken: accessTokenJwt || idTokenJwt });
    const session = new CognitoUserSession({ IdToken: idToken, AccessToken: accessToken });
    const pool = new CognitoUserPool({ UserPoolId: userPoolID, ClientId: clientID });
    const username = idToken.payload.email || idToken.payload.sub;
    const user = new CognitoUser({ Username: username, Pool: pool });

    const keyPrefix = `CognitoIdentityServiceProvider.${clientID}`;
    localStorage.setItem(`${keyPrefix}.${username}.idToken`, idTokenJwt);
    localStorage.setItem(`${keyPrefix}.${username}.accessToken`, accessTokenJwt || idTokenJwt);
    localStorage.setItem(`${keyPrefix}.${username}.clockDrift`, '0');
    if (refreshTokenJwt) {
      localStorage.setItem(`${keyPrefix}.${username}.refreshToken`, refreshTokenJwt);
    }
    localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);

    runInAction(() => {
      this.ready = false;
      this.isAuthorized = null;
    });

    return this.checkAuthorization({ session, user, attributes: {} })
      .then(result => {
        Analytics.event(Analytics.EVENTS.SIGNIN, { user: this.userName, tenant: this.tenantID, method: 'sso' });
        Analytics.identify(this.userName, this.tenantID);
        return result;
      });
  };

  loginWithCode = async (code, redirectUri) => {
    const verifier = retrieveAndClearVerifier();
    if (!verifier) {
      throw new Error('PKCE verifier missing — possible replay or tab mismatch');
    }
    const { AWSCognito: { authDomain, clientID } } = getConfig();
    const response = await fetch(`https://${authDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientID,
        code,
        code_verifier: verifier,
        redirect_uri: redirectUri,
      }).toString(),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error_description || data.error || 'Token exchange failed');
    }
    return this.loginWithTokens(data.id_token, data.access_token, data.refresh_token);
  };

  fetchUsage = async () => {
    const url = new URL(ExternalRESTAPI.Usage, window.location.origin);
    if (this.checkingAuthorization) {
      return this.checkingAuthorization.then(this.fetchUsage);
    }
    return new RequestBuilder(url)
      .withAuthorization(this.authData)
      .fetch()
      .then(resp => resp.json());
  }

  checkAuthorization = async ({session, user, attributes}, forceRevalidate = false) => {
    const tokenExpired = isSessionExpired(session);
    runInAction(() => {
      this.session = session;
      this.user = user;
      this.apiKey = attributes && attributes[API_KEY];
      this.tokenExpired = tokenExpired;
    });
    if (this.checkingAuthorization) {
      return this.checkingAuthorization;
    }
    // If forceRevalidate is true (e.g., after 401/403 error) or the token's own exp
    // claim has passed, we must re-validate authorization even if we think we're
    // already authorized. A cached isAuthorized=true from a prior run would otherwise
    // mask an actually-expired JWT until the first real API call 401s.
    if (this.isAuthorized != null && !forceRevalidate && !tokenExpired) {
      this.ready = true;
      return this.isAuthorized;
    }
    return this.checkingAuthorization = Promise.all([
      this._appStatusStore.fetchDatasetMetadata(this),
      this._appStatusStore.fetchMetadata(false, this),
    ])
      .then(() => {
        runInAction(() => {
          this.isAuthorized = true;
          this.refreshUICookie();
        });
        Sentry.getCurrentScope().setUser({email: this.userName});
        if (this.userName) {
          localStorage.setItem('plex-last-username', this.userName);
        }
        return session;
      })
      .catch(error => {
        runInAction(() => {
          this.isAuthorized = false;
        });
        throw error;
      })
      .finally(() => {
        runInAction(() => {
          this.checkingAuthorization = false;
          this.ready = true;
        });
      })
  };

  restoreSession = async (refresh = false, forceRefresh = false) => {
    if (this.session && !refresh) {
      console.debug('[UserStore] Using cached session, skipping restore');
      return this.session;
    }
    if (!this.authProvider) {
      console.debug('[UserStore] Auth provider not initialized, initializing first');
      return this._initProvider(refresh).then(() => this.restoreSession(refresh, forceRefresh));
    }
    // Convert multiple simultaneous calls into a single call
    if (this.restoring) {
      console.debug('[UserStore] Session restore already in progress, waiting');
      return this.restoring;
    }
    // Do not set session/isAuthorized values until the request has completed
    // to avoid transient changes when session is refreshed and maintained
    console.debug('[UserStore] Starting session restore', { refresh, forceRefresh });
    runInAction(() => {
      this.ready = false;
      this.tokenExpired = true;
    });
    return this.restoring = this.authProvider.retrieveUserSession(forceRefresh)
      .then(session => {
        if (!session) {
          console.debug('[UserStore] No session returned from retrieveUserSession');
          return null;
        }
        console.debug('[UserStore] Session retrieved, checking authorization');
        return this.checkAuthorization(session, forceRefresh);
      })
      .then(session => {
        if (session) {
          console.debug('[UserStore] Session restored successfully for', this.userName);
          Analytics.event(Analytics.EVENTS.SESSION_RESTORE, { user: this.userName, tenant: this.tenantID });
          Analytics.identify(this.userName, this.tenantID);
        } else {
          console.debug('[UserStore] Session restore completed but no valid session');
        }
        return session;
      })
      .catch(this.onSessionError)
      .finally(() => {
        runInAction(() => {
          this.restoring = false;
          this.ready = true;
          if (this.isAuthorized === null) {
            this.isAuthorized = false;
          }
        });
        console.debug('[UserStore] Session restore completed', {
          isAuthorized: this.isAuthorized,
          hasSession: !!this.session,
          userName: this.userName
        });
      })
  };

  onSessionError = async (error) => {
    console.debug('[UserStore] Session error occurred', {
      errorName: error?.name,
      errorMessage: error?.message,
      errorType: error?.constructor?.name
    });

    if (error?.name === "TooManyRequestsException") {
      console.debug('[UserStore] Rate limited, retrying in 1s');
      // this.restoring still points at the in-flight promise this handler is a
      // continuation of — clearing it first avoids restoreSession() returning that
      // same (still-unsettled) promise to itself, which is a chaining cycle.
      return wait(1000).then(() => {
        runInAction(() => { this.restoring = false; });
        return this.restoreSession(true);
      });
    }
    // NetworkError is a browser-level fetch failure (offline, DNS, CORS) — a transient condition.
    // Retry once (guarded by _networkErrorRetried) then give up silently.
    if (error?.code === 'NetworkError') {
      if (!this._networkErrorRetried) {
        this._networkErrorRetried = true;
        console.warn('[UserStore] Network error during session restore, retrying once in 2s');
        Analytics.event(Analytics.EVENTS.SESSION_RESTORE, { status: 'failed', reason: 'network-error', retry: true });
        return wait(2000).then(() => {
          runInAction(() => { this.restoring = false; });
          return this.restoreSession(true);
        });
      }
      this._networkErrorRetried = false;
      console.warn('[UserStore] Network error on retry, giving up');
      Analytics.event(Analytics.EVENTS.SESSION_RESTORE, { status: 'failed', reason: 'network-error', retry: false });
      toast.error("Unable to reach the authentication server (cognito-idp.us-east-1.amazonaws.com). Check your network connection, or ask your IT team to allow access to this address.");
      // fall through to session cleanup below
    }
    // Clear any partial Cognito session data that might have been created during failed login
    if (this.authProvider) {
      // Sign out any lingering session in Cognito localStorage
      this.authProvider.signOut(null);
    }
    this.session = null;
    this.user = null;
    this.isAuthorized = false;
    this.ready = true;
    // NotAuthorizedException with expired refresh token is an expected condition
    // when the user's session has expired - don't re-throw, just return null session
    // NotAuthorized (403 from API) is also expected when user lacks authorization
    // NetworkError after force-refresh also falls here — transient, user stays on page
    if (error?.name === "NotAuthorizedException" || error instanceof NotAuthorized || error?.code === 'NetworkError') {
      console.debug("[UserStore] Session could not be restored, user needs to re-authenticate", error.message);
      return null;
    }
    console.error('[UserStore] Unexpected session error, re-throwing', error);
    throw error;
  }

  register = (username, password) => {
    // TODO: excise +.* suffixes and dots from gmail
    Analytics.event(Analytics.EVENTS.REGISTER, { user: username });
    const register = provider => provider.register(username, password)
      .catch(error => {
        Analytics.event(Analytics.EVENTS.REGISTER_FAILURE, { user: username, error_name: error?.name });
        throw error;
      });
    return this.authProvider ? register(this.authProvider) : this._initProvider().then(register);
  };

  confirmRegistration = (username, code) => {
    Analytics.event(Analytics.EVENTS.CONFIRM_REGISTRATION, { user: username });
    const confirm = provider => provider.confirmRegistration(username, code)
      .catch(error => {
        if (error.message?.indexOf("CONFIRMED") !== -1) {
          return;
        }
        throw error;
      });
    return this.authProvider ? confirm(this.authProvider) : this._initProvider().then(confirm);
  };

  resendConfirmation = username => {
    Analytics.event(Analytics.EVENTS.RESEND_CONFIRMATION, { user: username });
    const resend = provider => provider.resendConfirmation(username);
    return this.authProvider ? resend(this.authProvider) : this._initProvider().then(resend);
  };

  sendVerificationCode = username => {
    Analytics.event(Analytics.EVENTS.PASSWORD_RESET, { user: username });
    const send = provider => provider.sendVerificationCode(username);
    return this.authProvider ? send(this.authProvider) : this._initProvider(send);
  };

  changePassword = (username, oldPassword, newPassword, navigate) => {
    Analytics.event(Analytics.EVENTS.PASSWORD_CHANGE, { user: username });
    const change = provider => provider.changePassword(username, oldPassword, newPassword, navigate);
    const p = this.authProvider ? change(this.authProvider) : this._initProvider(change);
    return p.then(({session, user}) => {
        runInAction(() => {
          this.session = session;
          this.user = user;
        });
        Sentry.getCurrentScope().setUser({email: this.userName});
        return registerPasswordChange(this.authData, newPassword)
          .then(() => {
            toast.success("Password updated");
          });
      })
      .catch(error => {
        // e.g. 400: name="LimitExceededException"
        throw error;
      });
    };

  runServerPasswordValidation = (username, password, reset) => {
    return fetchPasswordValidation(username, password, reset);
  };

  confirmNewPassword = (username, verificationCode, newPassword) => {
    const confirm = provider => provider.confirmNewPassword(username, verificationCode, newPassword);
    return (this.authProvider ? confirm(this.authProvider) : this._initProvider().then(confirm))
      .catch(error => {
        throw error;
      });
  };

  generateAPIKey = () => {
    const req = new RequestBuilder(ExternalRESTAPI.APIKey);
    return req
      .asPOST()
      .withAuthorization(this.authData)
      .fetch()
      .then(resp => resp.json())
      .then(apiKey => {
        runInAction(() => this.apiKey = apiKey);
        return apiKey;
      });
  };

  get ready() {
    return this._ready;
  }

  set ready(r) {
    this._ready = r;
  }

  get checkingAuthorization() {
    return this._checkingAuthorization;
  }

  set checkingAuthorization(checking) {
    this._checkingAuthorization = checking;
  }

  get restoring() {
    return this._restoring;
  }

  set restoring(r) {
    this._restoring = r;
  }

  refresh = async (errorResponse = null) => {
    if (errorResponse && errorResponse.status !== 401 && errorResponse.status !== 403) {
      console.error("Unrecoverable error", errorResponse);
      throw errorResponse;
    }
    // restoreSession() already dedupes concurrent calls via _restoring, but each caller
    // here was still racing its *own* 30s timer against that shared promise: if the
    // underlying Cognito call hung, every concurrent 401 independently timed out and
    // surfaced its own "session expired" error. Share one refresh() attempt/timer instead.
    if (this._refreshPromise) {
      console.debug('[UserStore] Refresh already in progress, waiting');
      return this._refreshPromise;
    }
    const oldToken = this.jwtToken;
    console.debug("Refresh auth tokens due to 401/403 error", errorResponse, `old token: ${oldToken?.substring(0, 20)}...`);
    // Force a real token refresh since we know the current token is expired/invalid
    // This ensures we call Cognito's refreshSession() explicitly rather than relying
    // on the SDK's implicit refresh logic, and validates the new token works
    //
    // Cognito SDK callbacks are Promise-wrapped with no internal timeout; on a degraded
    // network the restore can hang indefinitely. Race against a 30s deadline so we surface
    // NotAuthorized (→ "session expired") instead of waiting for POST_TIMEOUT (120s) to
    // fire and produce the misleading "Request timed out" error (WEBAPP-10).
    const REFRESH_TIMEOUT_MS = 30000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new NotAuthorized("Token refresh timed out")), REFRESH_TIMEOUT_MS));
    this._refreshPromise = Promise.race([this.restoreSession(true, true), timeoutPromise])
      .then(session => {
        const newToken = this.jwtToken;
        if (!newToken) {
          console.error("Token refresh failed - no token returned");
          throw new NotAuthorized("Token refresh failed - no token returned");
        }
        if (oldToken && newToken === oldToken) {
          console.error(`Token refresh returned same token: ${oldToken?.substring(0, 20)}...`);
          throw new NotAuthorized("Token refresh failed - same token returned");
        }
        console.debug("Session refreshed with new token", `new token: ${newToken?.substring(0, 20)}...`);
        return this.authData;
      })
      .finally(() => {
        this._refreshPromise = null;
        // Promise.race doesn't cancel the losing side: if we hit here via the timeout,
        // restoreSession()'s underlying retrieveUserSession() call is still pending and
        // _restoring is still true. Left alone, that abandoned promise would permanently
        // wedge every future restoreSession() caller (login retry, useTokenRefresh,
        // OfflineDetect) behind a promise that will never settle. Clear it so the next
        // attempt actually starts fresh.
        if (this.restoring) {
          runInAction(() => {
            this.restoring = false;
            this.ready = true;
          });
        }
      });
    return this._refreshPromise;
  }

  get isAdmin() {
    return this.userName && this.userName.endsWith("@plexresearch.com") || this.userGroups.includes('admins');
  }

  getJWTPayload = (token) => {
      const base64URL = token.split('.')[1]
      const base64 = base64URL.replace(/-/g, '+').replace(/_/g, '/');
      const p = decodeURIComponent(atob(base64)
                                     .split('')
                                     .map(c => '%' + ('00' + c.charCodeAt(0).toString(16))
                                       .slice(-2)).join(''));
      return JSON.parse(p);
  }

  get isSSOUser() {
    return this._loginMethod === 'sso';
  }

  get isPowerUser() {
    return this.userGroups.includes('power-users')
  }

  get showAdmin() {
    return this.isAdmin && this._showAdmin;
  }

  set showAdmin(show) {
    this._showAdmin = localStorage['plex-show-admin'] = show;
  }

  get userName() {
    return this.idToken ? toJS(this.idToken.payload.email) : null;
  }

  get userGroups() {
    return this.accessToken?.payload['cognito:groups'] || [];
  }

  get apiKey() {
    return toJS(this._apiKey);
  }

  set apiKey(key) {
    this._apiKey = key;
  }

  refreshUICookie() {
    const token = this.jwtToken;
    if (token) {
      document.cookie = `plex_ui_key=${encodeURIComponent("Bearer " + token)}; path=/api; max-age=3600; SameSite=Lax${DEV ? "" : "; Secure"}`;
    } else {
      document.cookie = `plex_ui_key=; path=/api; max-age=0; SameSite=Lax${DEV ? "" : "; Secure"}`;
    }
  }

  get userID() {
    return toJS(this.idToken?.payload.sub);
  }

  get tenantID() {
    return toJS(this.idToken?.payload.tenant_id);
  }

  get jwtToken() {
    return this.idToken?.jwtToken;
  }

  get isAuthenticated() {
    return !!this.session;
  }

  get isAuthorized() {
    return this._isAuthorized;
  }

  set isAuthorized(auth) {
    this._isAuthorized = auth;
  }

  get tokenExpired() {
    return toJS(this._tokenExpired);
  }

  set tokenExpired(expired) {
    this._tokenExpired = expired;
  }

  get session() {
    return toJS(this._cognitoUserSession);
  }

  set session(s) {
    this._cognitoUserSession = s;
  }

  get user() {
    return toJS(this._cognitoUser);
  }

  set user(user) {
    this._cognitoUser = user;
  }

  get authProvider() {
    return toJS(this._authProvider);
  }

  set authProvider(p) {
    this._authProvider = p;
  }

  get accessToken() {
    return this.session?.accessToken;
  }

  get idToken() {
    return this.session?.idToken;
  }

  get expiredAt() {
    return toJS(this.idToken?.payload.exp);
  }

  get isTokenExpired() {
    return toJS(this._tokenExpired);
  }

  get initializing() {
    return this.authProvider === null;
  }
}

export default UserStore;
